
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';
import { useOrganizationsQuery } from '@/hooks/queries/useOrganizationsQuery';
import { useOrganizationSubscription } from '@/hooks/useOrganizationSubscription';
import { needsProfileSetup } from '@/utils/auth/auth-sync';

/**
 * Simple redirect component for the Today page
 * - If user needs profile setup, redirects to user setup
 * - If user has no organizations, redirects to organization setup
 * - If organization needs subscription setup, redirects to subscription page
 * - If user has one organization, redirects to that org's Today page
 * - If user has multiple organizations, redirects to organizations page
 */
export function TodayRedirect() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();

  // Use React Query to fetch organizations - it handles deduplication internally
  // MainLayout pre-fetches this, so it will usually be cached already
  const { data: orgs = [], isLoading: isOrgsLoading } = useOrganizationsQuery({
    enabled: isAuthenticated,
  });
  
  // Get subscription status for the organization (only check if we have exactly one org)
  // Only check when authenticated AND we have a single org to avoid race conditions
  const singleOrgId = orgs.length === 1 ? orgs[0]?.id : undefined;
  const {
    needsSubscription,
    isLoading: isSubLoading,
    isFetched: isSubFetched,
    error: subscriptionError
  } = useOrganizationSubscription(
    singleOrgId,
    { enabled: isAuthenticated && !!singleOrgId }
  );

  useEffect(() => {
    if (isLoading || !isAuthenticated || isOrgsLoading) {
      return;
    }

    if (user && needsProfileSetup(user)) {
      navigate('/user-setup');
      return;
    }

    if (orgs.length === 0) {
      navigate('/organization-setup');
      return;
    }

    if (orgs.length === 1 && orgs[0]?.slug) {
      // Check if user is suspended or inactive in this organization
      const membership = orgs[0].userMembership;
      if (membership?.status === 'SUSPENDED' || membership?.status === 'INACTIVE') {
        navigate('/organizations?suspended=true');
        return;
      }

      // If we're still loading subscription data, wait
      if (isSubLoading) {
        return;
      }

      // CRITICAL: Only make subscription decision when isFetched is true
      // isFetched = true means we have a definitive answer (success or 404)
      // isFetched = false means we don't know (error, still loading, disabled)
      // If not fetched, proceed to today and let OrgRouteGuard handle it
      if (!isSubFetched) {
        // subscriptionError is checked but no action needed - proceed to today page
        navigate(`/${orgs[0].slug}/today`);
        return;
      }

      // At this point, isFetched=true, so we have a definitive answer
      if (needsSubscription) {
        navigate(`/${orgs[0].slug}/subscription-setup`);
        return;
      }

      // Navigate to today if subscription is active
      navigate(`/${orgs[0].slug}/today`);
      return;
    }

    // Multiple organizations - let user choose, but they'll be checked individually
    // when they select an organization
    navigate('/organizations');
  }, [navigate, orgs, isLoading, isOrgsLoading, isAuthenticated, user, needsSubscription, isSubLoading, isSubFetched, subscriptionError]);

  return null;
}